// src/services/settingsService.js
import { apiRequest, uploadFile, downloadFile } from './api';

const SETTINGS_BASE_URL = '/settings';

/**
 * Settings Service
 * Handles all system settings and email template operations
 */
export const settingsService = {
    /**
     * Get all system settings
     */
    getSystemSettings: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/system`);
            return response.data;
        } catch (error) {
            console.error('Error fetching system settings:', error);
            throw error;
        }
    },

    /**
     * Update system settings
     * @param {Array} settings - Array of setting objects with setting_key and setting_value
     */
    updateSystemSettings: async (settings) => {
        try {
            const response = await apiRequest.put(`${SETTINGS_BASE_URL}/system`, {
                settings
            });
            return response.data;
        } catch (error) {
            console.error('Error updating system settings:', error);
            throw error;
        }
    },

    /**
     * Upload logo
     * @param {File} file - Logo file
     * @param {Function} onProgress - Progress callback
     */
    uploadLogo: async (file, onProgress = null) => {
        try {
            const formData = new FormData();
            formData.append('logo', file);

            const response = await uploadFile(
                `${SETTINGS_BASE_URL}/upload-logo`,
                formData,
                onProgress
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw error;
        }
    },

    /**
     * Upload favicon
     * @param {File} file - Favicon file
     * @param {Function} onProgress - Progress callback
     */
    uploadFavicon: async (file, onProgress = null) => {
        try {
            const formData = new FormData();
            formData.append('favicon', file);

            const response = await uploadFile(
                `${SETTINGS_BASE_URL}/upload-favicon`,
                formData,
                onProgress
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading favicon:', error);
            throw error;
        }
    },

    /**
     * Get all email templates
     */
    getEmailTemplates: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/email-templates`);
            return response.data;
        } catch (error) {
            console.error('Error fetching email templates:', error);
            throw error;
        }
    },

    /**
     * Get single email template
     * @param {number} id - Template ID
     */
    getEmailTemplate: async (id) => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/email-template/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching email template:', error);
            throw error;
        }
    },

    /**
     * Update email template
     * @param {number} id - Template ID
     * @param {Object} templateData - Template data (subject, body_html, body_text, is_active)
     */
    updateEmailTemplate: async (id, templateData) => {
        try {
            const response = await apiRequest.put(
                `${SETTINGS_BASE_URL}/email-template/${id}`,
                templateData
            );
            return response.data;
        } catch (error) {
            console.error('Error updating email template:', error);
            throw error;
        }
    },

    /**
     * Process pending email queue items (Core Admin)
     * @param {number} limit
     */
    processPendingEmailQueue: async (limit = 100) => {
        try {
            const response = await apiRequest.put(`${SETTINGS_BASE_URL}/email-templates`, { limit });
            return response.data;
        } catch (error) {
            console.error('Error processing pending email queue:', error);
            throw error;
        }
    },

    getEmailQueueStats: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/email-queue-stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching email queue stats:', error);
            throw error;
        }
    },

    runMaintenance: async (mode = 'full', queue_limit = 200) => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/maintenance-run`, { mode, queue_limit });
            return response.data;
        } catch (error) {
            console.error('Error running maintenance:', error);
            throw error;
        }
    },

    runIntegrationSync: async () => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/integration-sync-run`, {});
            return response.data;
        } catch (error) {
            console.error('Error running integration sync:', error);
            throw error;
        }
    },

    getIntegrationApiKeyConfig: async (orgId) => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/integration-api-keys`, {
                params: { org_id: orgId }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching integration API key config:', error);
            throw error;
        }
    },

    rotateIntegrationApiKey: async (orgId, status = 1) => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/integration-api-keys`, {
                org_id: orgId,
                status
            });
            return response.data;
        } catch (error) {
            console.error('Error rotating integration API key:', error);
            throw error;
        }
    },

    getIntegrationApiKeysList: async (params = {}) => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/integration-api-keys-list`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching integration API keys list:', error);
            throw error;
        }
    },

    runBackup: async (payload = {}) => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/backup-run`, payload, {
                timeout: 10 * 60 * 1000 // 10 minutes for large archives
            });
            return response.data;
        } catch (error) {
            console.error('Error running backup:', error);
            throw error;
        }
    },

    getBackups: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/backups`);
            return response.data;
        } catch (error) {
            console.error('Error fetching backups:', error);
            throw error;
        }
    },

    downloadBackupFile: async (filename, onProgress = null) => {
        return downloadFile(`${SETTINGS_BASE_URL}/backup-download?kind=portal&file=${encodeURIComponent(filename)}`, filename, onProgress);
    },

    runDatabaseBackup: async () => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/db-backup-run`, {}, {
                timeout: 10 * 60 * 1000
            });
            return response.data;
        } catch (error) {
            console.error('Error running database backup:', error);
            throw error;
        }
    },

    getDatabaseBackups: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/db-backups`);
            return response.data;
        } catch (error) {
            console.error('Error fetching database backups:', error);
            throw error;
        }
    },

    downloadDatabaseBackupFile: async (filename, onProgress = null) => {
        return downloadFile(`${SETTINGS_BASE_URL}/backup-download?kind=db&file=${encodeURIComponent(filename)}`, filename, onProgress);
    },

    getDatabaseBackupConfig: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/db-backup-config`);
            return response.data;
        } catch (error) {
            console.error('Error fetching database backup config:', error);
            throw error;
        }
    },

    updateDatabaseBackupConfig: async (payload) => {
        try {
            const response = await apiRequest.put(`${SETTINGS_BASE_URL}/db-backup-config`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating database backup config:', error);
            throw error;
        }
    },

    getBackupCloudConfig: async () => {
        try {
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/backup-cloud-config`);
            return response.data;
        } catch (error) {
            console.error('Error fetching backup cloud config:', error);
            throw error;
        }
    },

    updateBackupCloudConfig: async (payload) => {
        try {
            const response = await apiRequest.put(`${SETTINGS_BASE_URL}/backup-cloud-config`, payload);
            return response.data;
        } catch (error) {
            console.error('Error updating backup cloud config:', error);
            throw error;
        }
    },

    testBackupCloudWebhook: async () => {
        try {
            const response = await apiRequest.post(`${SETTINGS_BASE_URL}/backup-cloud-test`, {});
            return response.data;
        } catch (error) {
            console.error('Error testing backup cloud webhook:', error);
            throw error;
        }
    },

    /**
     * Get public settings (logo, favicon) - no authentication required
     */
    getPublicSettings: async () => {
        try {
            // This would need a separate public endpoint in the API
            // For now, we'll handle it in the component
            const response = await apiRequest.get(`${SETTINGS_BASE_URL}/public`);
            return response.data;
        } catch (error) {
            console.error('Error fetching public settings:', error);
            // Return defaults if API fails
            return {
                logo: null,
                favicon: null
            };
        }
    },

    // ==================== COUNTRY MANAGEMENT ====================
    getCountries: async (params = {}) => {
        try {
            const response = await apiRequest.get('/countries', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch countries');
        }
    },

    createCountry: async (data) => {
        try {
            const response = await apiRequest.post('/countries', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create country');
        }
    },

    updateCountry: async (id, data) => {
        try {
            const response = await apiRequest.put(`/countries/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update country');
        }
    },

    deleteCountry: async (id) => {
        try {
            const response = await apiRequest.delete(`/countries/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to delete country');
        }
    },

    // ==================== CITY MANAGEMENT ====================
    getCities: async (params = {}) => {
        try {
            const response = await apiRequest.get('/cities', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch cities');
        }
    },

    createCity: async (data) => {
        try {
            const response = await apiRequest.post('/cities', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create city');
        }
    },

    updateCity: async (id, data) => {
        try {
            const response = await apiRequest.put(`/cities/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update city');
        }
    },

    deleteCity: async (id) => {
        try {
            const response = await apiRequest.delete(`/cities/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to delete city');
        }
    },

    // ==================== DESIGNATION MANAGEMENT ====================
    getDesignations: async (params = {}) => {
        try {
            const response = await apiRequest.get('/designations', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch designations');
        }
    },

    createDesignation: async (data) => {
        try {
            const response = await apiRequest.post('/designations', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create designation');
        }
    },

    updateDesignation: async (id, data) => {
        try {
            const response = await apiRequest.put(`/designations/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update designation');
        }
    },

    deleteDesignation: async (id) => {
        try {
            const response = await apiRequest.delete(`/designations/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to delete designation');
        }
    },

    // ==================== PUBLIC HOLIDAY MANAGEMENT ====================
    getPublicHolidays: async (params = {}) => {
        try {
            const response = await apiRequest.get('/public-holidays', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch holidays');
        }
    },

    createPublicHoliday: async (data) => {
        try {
            const response = await apiRequest.post('/public-holidays', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create holiday');
        }
    },

    updatePublicHoliday: async (id, data) => {
        try {
            const response = await apiRequest.put(`/public-holidays/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update holiday');
        }
    },

    deletePublicHoliday: async (id) => {
        try {
            const response = await apiRequest.delete(`/public-holidays/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to delete holiday');
        }
    },

    // ==================== SALES AGENT MANAGEMENT ====================
    getSalesAgents: async (params = {}) => {
        try {
            const response = await apiRequest.get('/sales-agents', { params });
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch sales agents');
        }
    },

    createSalesAgent: async (data) => {
        try {
            const response = await apiRequest.post('/sales-agents', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to create sales agent');
        }
    },

    updateSalesAgent: async (id, data) => {
        try {
            const response = await apiRequest.put(`/sales-agents/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to update sales agent');
        }
    },

    deleteSalesAgent: async (id) => {
        try {
            const response = await apiRequest.delete(`/sales-agents/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to delete sales agent');
        }
    },

    getSalesAgentDetails: async (id) => {
        try {
            const response = await apiRequest.get(`/sales-agents/${id}/details`);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to fetch sales agent details');
        }
    },

    assignSalesAgentToOrganization: async (data) => {
        try {
            const response = await apiRequest.post('/sales-agents/assign', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to assign sales agent to organization');
        }
    }
};

export default settingsService;
